import {createEntityMeta,SyncStatus} from '../offline/entity.js';

export const WOOD_INVENTORY_ENTITY='wood_inventory';
export const WoodQuantityUnit=Object.freeze({
  GRAM:'g',
  KILOGRAM:'kg',
  MILLILITRE:'ml',
  LITRE:'l',
  PIECE:'pcs'
});

const UNITS=new Set(Object.values(WoodQuantityUnit));
const KEY_PATTERN=/^[a-z][a-z0-9_]{1,63}$/;

function required(value,label,maxLength){
  const result=String(value||'').trim();
  if(!result)throw new TypeError(`${label} is required`);
  if(result.length>maxLength)throw new TypeError(`${label} is too long`);
  return result;
}

function optionalText(value,maxLength){
  if(value==null||value==='')return null;
  const result=String(value).trim();
  if(result.length>maxLength)throw new TypeError('Text value is too long');
  return result||null;
}

function stableKey(value,label){
  if(value==null||value==='')return null;
  const key=String(value).trim().toLowerCase();
  if(!KEY_PATTERN.test(key))throw new TypeError(`Invalid ${label}`);
  return key;
}

function resolveQuantity(quantity,unit){
  if(quantity==null||quantity===''){
    if(unit!=null&&unit!=='')throw new TypeError('quantityUnit requires quantity');
    return Object.freeze({quantity:null,quantityUnit:null});
  }
  const number=Number(quantity);
  if(!Number.isFinite(number)||number<0)throw new TypeError('quantity must be a non-negative number');
  const resolvedUnit=String(unit||'').trim().toLowerCase();
  if(!UNITS.has(resolvedUnit))throw new TypeError('Unsupported quantityUnit');
  return Object.freeze({quantity:number,quantityUnit:resolvedUnit});
}

function meta(input={}){
  if(input.id){
    return Object.freeze({
      id:String(input.id),
      createdAt:String(input.createdAt||new Date().toISOString()),
      updatedAt:String(input.updatedAt||input.createdAt||new Date().toISOString()),
      deletedAt:input.deletedAt?String(input.deletedAt):null,
      syncStatus:input.syncStatus||SyncStatus.SYNCED,
      serverVersion:Number(input.serverVersion??input.version??0)||0
    });
  }
  return createEntityMeta({now:input.now||new Date().toISOString()});
}

export function createWoodInventoryItem({
  id,
  userId,
  catalogueKey=null,
  customName=null,
  available=true,
  quantity=null,
  quantityUnit=null,
  notes=null,
  archivedAt=null,
  createdAt,
  updatedAt,
  deletedAt=null,
  syncStatus,
  serverVersion=0,
  now
}={}){
  const key=stableKey(catalogueKey,'catalogueKey');
  const custom=optionalText(customName,120);
  if(Boolean(key)===Boolean(custom))throw new TypeError('Exactly one of catalogueKey or customName is required');
  const amount=resolveQuantity(quantity,quantityUnit);
  const m=meta({id,createdAt,updatedAt,deletedAt,syncStatus,serverVersion,now});
  return Object.freeze({
    ...m,
    userId:required(userId,'userId',128),
    catalogueKey:key,
    customName:custom,
    available:Boolean(available),
    quantity:amount.quantity,
    quantityUnit:amount.quantityUnit,
    notes:optionalText(notes,2000),
    archivedAt:archivedAt?String(archivedAt):null
  });
}

export function updateWoodInventoryItem(item,changes={}){
  const current=createWoodInventoryItem(item);
  return createWoodInventoryItem({
    ...current,
    ...changes,
    id:current.id,
    userId:current.userId,
    createdAt:current.createdAt,
    updatedAt:changes.updatedAt||current.updatedAt,
    syncStatus:current.syncStatus,
    serverVersion:current.serverVersion
  });
}

export function archiveWoodInventoryItem(item,{now=new Date().toISOString()}={}){
  return updateWoodInventoryItem(item,{available:false,archivedAt:now,updatedAt:now});
}

export function restoreWoodInventoryItem(item,{now=new Date().toISOString()}={}){
  return updateWoodInventoryItem(item,{archivedAt:null,updatedAt:now});
}

export function woodInventoryToRow(item){
  const value=createWoodInventoryItem(item);
  return Object.freeze({
    id:value.id,
    user_id:value.userId,
    wood_key:value.catalogueKey,
    custom_name:value.customName,
    available:value.available,
    quantity:value.quantity,
    quantity_unit:value.quantityUnit,
    notes:value.notes,
    archived_at:value.archivedAt,
    version:Math.max(1,value.serverVersion||1)
  });
}

export function woodInventoryFromRow(row={}){
  return createWoodInventoryItem({
    id:row.id,
    userId:row.user_id,
    catalogueKey:row.wood_key,
    customName:row.custom_name,
    available:row.available,
    quantity:row.quantity,
    quantityUnit:row.quantity_unit,
    notes:row.notes,
    archivedAt:row.archived_at,
    createdAt:row.created_at,
    updatedAt:row.updated_at,
    serverVersion:row.version,
    syncStatus:SyncStatus.SYNCED
  });
}
